import { useEffect } from 'react'
import { DrawerForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { Alert, App, Form, Input, Select } from 'antd'
import { DRAWER_SIZE } from '@/const/drawer.ts'
import { ROOM_STATUS_OPTIONS } from '@/features/master-data/const/room-editor-modal.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { type RoomType } from './room-editor-modal.tsx'
import { RoomGenerationFields } from './room-generation-fields.tsx'
import { buildRooms } from './room-generation.ts'

export type RoomBatchFormValues = {
  siteId: number
  mode: 'range' | 'list'
  prefix?: string
  suffix?: string
  startNumber?: number
  endNumber?: number
  padStart?: number
  listText?: string
  floor?: string
  roomType: RoomType
  area?: number
  status: 'vacant' | 'reserved' | 'occupied' | 'maintenance' | 'disabled'
  note?: string
}

export function RoomBatchModal({
  defaultSiteId,
  open,
  onClose,
  onSaved,
  siteLocked = false,
  siteOptions
}: {
  defaultSiteId?: number
  open: boolean
  onClose: () => void
  onSaved: () => Promise<void>
  siteLocked?: boolean
  siteOptions: Array<{ label: string; value: number }>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<RoomBatchFormValues>()
  const batchCreate = useMutation(trpc.room.batchCreate.mutationOptions())

  useEffect(() => {
    if (!open) {
      return
    }
    form.resetFields()
    form.setFieldsValue({
      mode: 'range',
      roomType: 'office',
      siteId: defaultSiteId,
      status: 'vacant'
    })
  }, [defaultSiteId, form, open])

  const save = async (values: RoomBatchFormValues) => {
    let rooms
    try {
      rooms = buildRooms(values)
    } catch (error) {
      void message.warning(getErrorMessage(error, '请检查房间编号'))
      return false
    }
    if (rooms.length === 0) {
      void message.warning('请至少填写一个房间号')
      return false
    }
    if (rooms.length > 500) {
      void message.warning('单次最多生成 500 个房间')
      return false
    }
    try {
      const result = await batchCreate.mutateAsync({
        floor: values.floor?.trim() || null,
        note: values.note?.trim() || null,
        roomType: values.roomType,
        rooms,
        siteId: values.siteId,
        status: values.status
      })
      const skippedText = result.skipped.length > 0 ? `，跳过已存在 ${result.skipped.length} 个` : ''
      void message.success(`已生成 ${result.created} 个房间${skippedText}`)
      await onSaved()
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '批量生成房间失败'))
      return false
    }
  }

  return (
    <DrawerForm<RoomBatchFormValues>
      form={form}
      drawerProps={{ destroyOnHidden: true, mask: { closable: false }, onClose }}
      layout="vertical"
      onFinish={save}
      onOpenChange={(visible) => {
        if (!visible) {
          onClose()
        }
      }}
      open={open}
      scrollToFirstError
      submitter={{
        resetButtonProps: { onClick: onClose, size: 'medium' },
        searchConfig: { resetText: '取消', submitText: '批量生成' },
        submitButtonProps: { loading: batchCreate.isPending, size: 'medium' }
      }}
      title="批量生成房间"
      width={DRAWER_SIZE}
    >
      <Alert
        className="mb-4!"
        description={
          <ul className="list-disc space-y-1 pl-4">
            <li>先选择场地和生成方式；带红色 * 的字段为必填项。</li>
            <li>编号区间：填写前缀、起始和结束编号，可选后缀；例如 A-、1、3 会生成 A-1、A-2、A-3。</li>
            <li>需要 A-001 这样的编号时，把补零位数填 3；粘贴列表则每行填写一个房间号，可用空格或逗号附带面积。</li>
            <li>
              楼层、房间类型、初始状态和备注会应用到全部房间；列表中的单行面积优先。重复房间号会跳过，单次最多生成 500
              个。
            </li>
          </ul>
        }
        showIcon
        title="使用说明"
        type="info"
      />
      <Form.Item label="所属场地" name="siteId" rules={[{ message: '请选择场地', required: true }]}>
        <Select
          disabled={siteLocked}
          options={siteOptions}
          placeholder="请选择场地"
          showSearch={{ optionFilterProp: 'label' }}
        />
      </Form.Item>
      <RoomGenerationFields />
      <Form.Item label="初始状态" name="status" rules={[{ required: true }]}>
        <Select options={ROOM_STATUS_OPTIONS} placeholder="请选择初始状态" />
      </Form.Item>
      <Form.Item label="备注" name="note">
        <Input.TextArea maxLength={2000} placeholder="统一备注（选填）" rows={3} showCount />
      </Form.Item>
    </DrawerForm>
  )
}
